<template>
  <v-container fluid class="dashboard-container pa-0">
    <!-- Header del Dashboard -->
    <v-row no-gutters class="dashboard-header">
      <v-col cols="12">
        <div class="dashboard-header-content">
          <div class="dashboard-title">
            <div class="title-row">
              <img 
                src="@/assets/logo.png" 
                alt="Logo Empresa" 
                class="company-logo mr-3"
                @error="handleLogoError"
              />
              <h1 class="text-h4 font-weight-medium">
                Analytics
              </h1>
            </div>
            <p class="text-subtitle-2 mt-1">
              Business intelligence y métricas de rendimiento
            </p>
          </div>
          <div class="dashboard-actions">
            <!-- Selector de Período - Estilo Google Analytics 4 -->
            <v-select
              v-model="selectedPeriod"
              :items="periodOptions"
              item-text="title"
              item-value="value"
              solo
              dense
              class="period-selector mr-4"
              hide-details
              style="max-width: 160px"
              @change="handlePeriodChange"
            />
            
            <v-btn
              color="primary"
              prepend-icon="mdi-refresh"
              @click="refreshData"
              :loading="loading"
              size="small"
              variant="text"
              class="refresh-btn"
            >
              Actualizar
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Modal para Fechas Personalizadas -->
    <v-dialog v-model="showCustomDateDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4 pb-2">
          Seleccionar Período Personalizado
        </v-card-title>
        <v-card-text class="pa-4 pt-2">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="customStartDate"
                label="Fecha Inicio"
                type="date"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="customEndDate"
                label="Fecha Fin"
                type="date"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            text
            color="grey darken-1"
            @click="showCustomDateDialog = false"
            class="text-none"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="applyCustomDate"
            class="text-none"
            elevation="0"
          >
            Aplicar
          </v-btn>
        </v-card-actions>
      </v-card>
         </v-dialog>

    <!-- Métricas Principales - Layout Google Analytics 4 -->
    <v-row no-gutters class="metrics-container">
      <!-- Primera Fila - 4 tarjetas -->
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper blue-bg">
                <v-icon color="white" size="20">mdi-currency-usd</v-icon>
              </div>
              <div class="metric-title">Cotizado</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.cotizado) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper orange-bg">
                <v-icon color="white" size="20">mdi-check-circle</v-icon>
              </div>
              <div class="metric-title">Confirmado</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.confirmado) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper purple-bg">
                <v-icon color="white" size="20">mdi-flag-checkered</v-icon>
              </div>
              <div class="metric-title">Finalizado</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.finalizado) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper gray-bg">
                <v-icon color="white" size="20">mdi-file-document</v-icon>
              </div>
              <div class="metric-title">Finalizado Sin DTE</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.finalizadoSinDte) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Segunda Fila - 4 tarjetas -->
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper teal-bg">
                <v-icon color="white" size="20">mdi-receipt</v-icon>
              </div>
              <div class="metric-title">Facturado Neto</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.facturadoNeto) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper orange-bg">
                <v-icon color="white" size="20">mdi-cash-multiple</v-icon>
              </div>
              <div class="metric-title">Recaudado</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.recaudado) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper red-bg">
                <v-icon color="white" size="20">mdi-cart</v-icon>
              </div>
              <div class="metric-title">Compras Neto</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.comprasNeto) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3" class="metric-col">
        <v-card class="metric-card" height="120" elevation="1">
          <v-card-text class="pa-4">
            <div class="metric-header">
              <div class="metric-icon-wrapper purple-bg">
                <v-icon color="white" size="20">mdi-credit-card</v-icon>
              </div>
              <div class="metric-title">Compras Pagado</div>
            </div>
            <div class="metric-body">
              <div class="metric-value">${{ formatCurrency(metrics.comprasPagado) }}</div>
              <div class="metric-label">TOTAL DEL PERÍODO</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráfico de Tendencias de Ventas -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card class="trends-chart-card" elevation="1">
          <v-card-title class="chart-title">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="#1a73e8">mdi-chart-line</v-icon>
              <span class="text-h6 font-weight-medium">Tendencia de Ventas</span>
            </div>
            <v-spacer></v-spacer>
            <div class="chart-legend">
              <div class="legend-item">
                <div class="legend-color confirmed"></div>
                <span>Confirmadas</span>
              </div>
              <div class="legend-item">
                <div class="legend-color quoted"></div>
                <span>Cotizadas</span>
              </div>

            </div>
          </v-card-title>
          
          <v-card-text class="chart-container">
            <LineChart 
              :chart-data="chartData" 
              :options="chartOptions"
              :height="300"
            />
          </v-card-text>
        </v-card>
      </v-col>
            </v-row>
        
        <!-- Sección 3: Top Performers -->
        <v-row class="mt-6">
          <!-- Top 5 Líneas de Productos -->
          <v-col cols="12" md="4">
            <v-card class="trends-chart-card" elevation="1">
              <v-card-title class="chart-title">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="#1a73e8">mdi-trophy</v-icon>
                  <span class="text-h6 font-weight-medium">Top 5 Líneas</span>
                </div>
              </v-card-title>
              
              <v-card-text class="pa-4">
                <div class="ga-table">
                  <div class="ga-header">
                    <div class="ga-rank">#</div>
                    <div class="ga-name">Nombre</div>
                    <div class="ga-value">Valor</div>
                    <div class="ga-percent">%</div>
                  </div>
                  <div 
                    v-for="(product, index) in topProductsList" 
                    :key="index"
                    class="ga-row"
                  >
                    <div class="ga-rank">{{ index + 1 }}</div>
                    <div class="ga-name">{{ product.name }}</div>
                    <div class="ga-value">${{ formatCurrency(product.revenue) }}</div>
                    <div class="ga-percent">
                      <div class="ga-bar">
                        <div class="ga-fill" :style="{ width: product.percentage + '%' }"></div>
                      </div>
                      <span>{{ product.percentage }}%</span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          
          <!-- Top 5 Clientes -->
          <v-col cols="12" md="4">
            <v-card class="trends-chart-card" elevation="1">
              <v-card-title class="chart-title">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="#1a73e8">mdi-account-group</v-icon>
                  <span class="text-h6 font-weight-medium">Top 5 Clientes</span>
                </div>
              </v-card-title>
              
              <v-card-text class="pa-4">
                <div class="ga-table">
                  <div class="ga-header">
                    <div class="ga-rank">#</div>
                    <div class="ga-name">Cliente</div>
                    <div class="ga-value">Facturación</div>
                    <div class="ga-percent">%</div>
                  </div>
                  <div 
                    v-for="(customer, index) in topCustomersList" 
                    :key="index"
                    class="ga-row"
                  >
                    <div class="ga-rank">{{ index + 1 }}</div>
                    <div class="ga-name">{{ customer.name }}</div>
                    <div class="ga-value">${{ formatCurrency(customer.revenue) }}</div>
                    <div class="ga-percent">
                      <div class="ga-bar">
                        <div class="ga-fill" :style="{ width: customer.percentage + '%' }"></div>
                      </div>
                      <span>{{ customer.percentage }}%</span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          
          <!-- Top 5 Vendedores -->
          <v-col cols="12" md="4">
            <v-card class="trends-chart-card" elevation="1">
              <v-card-title class="chart-title">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="#1a73e8">mdi-account-star</v-icon>
                  <span class="text-h6 font-weight-medium">Top 5 Vendedores</span>
                </div>
              </v-card-title>
              
              <v-card-text class="pa-4">
                <div class="ga-table">
                  <div class="ga-header">
                    <div class="ga-rank">#</div>
                    <div class="ga-name">Vendedor</div>
                    <div class="ga-value">Ventas</div>
                    <div class="ga-percent">%</div>
                  </div>
                  <div 
                    v-for="(seller, index) in topSellersList" 
                    :key="index"
                    class="ga-row"
                  >
                    <div class="ga-rank">{{ index + 1 }}</div>
                    <div class="ga-name">{{ seller.name }}</div>
                    <div class="ga-value">${{ formatCurrency(seller.revenue) }}</div>
                    <div class="ga-percent">
                      <div class="ga-bar">
                        <div class="ga-fill" :style="{ width: seller.percentage + '%' }"></div>
                      </div>
                      <span>{{ seller.percentage }}%</span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

    <!-- Información Adicional -->
    <v-row no-gutters class="info-row">
      <v-col cols="12">
        <v-card class="info-card" elevation="0">
          <v-card-text class="pa-4">
            <div class="info-content">
              <v-icon color="info" class="mr-3" size="20">mdi-information</v-icon>
              <span class="info-text">
                Última actualización: {{ lastUpdate }} | Período: {{ currentPeriod }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    </v-container>
  </template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import BarChart from '@/components/charts/BarChart.vue'
const numeral = require('numeral')

export default {
  name: 'Dashboard',
  components: {
    LineChart,
    BarChart
  },
  data: () => ({
    loading: false,
    selectedPeriod: 'current_month',
    
    // Opciones de período - Estilo SAP/Oracle
    periodOptions: [
      { title: 'Este Mes', value: 'current_month' },
      { title: 'Mes Anterior', value: 'last_month' },
      { title: 'Este Trimestre', value: 'current_quarter' },
      { title: 'Este Año', value: 'current_year' },
      { title: 'Personalizado', value: 'custom' }
    ],
    
    // Datos de las métricas
    metrics: {
      cotizado: 62048500,
      confirmado: 61071500,
      finalizado: 0,
      finalizadoSinDte: 0,
      facturadoNeto: 58071500,
      recaudado: 1534000,
      comprasNeto: 12995000,
      comprasPagado: 1870000
    },
    
    lastUpdate: '10:58 a. m.',
    currentPeriod: 'Enero 2024',
    showCustomDateDialog: false,
    customStartDate: null,
    customEndDate: null,
    chartData: {
      labels: [],
      datasets: []
    },
    chartOptions: {},
         topProductsList: [],
     topCustomersList: [],
     topSellersList: []
  }),
  
  watch: {
    selectedPeriod(newPeriod) {
      this.updatePeriodData(newPeriod)
    }
  },
  
  methods: {
         formatCurrency(value) {
       return numeral(value).format('0,0')
     },
     
     formatNumber(value) {
       return numeral(value).format('0,0')
     },
     

    
    refreshData() {
      this.loading = true
      // Simular carga de datos
      setTimeout(() => {
        this.loading = false
        this.lastUpdate = new Date().toLocaleTimeString('es-CL', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true
        })
      }, 1500)
    },
    
    updatePeriodData(period) {
      // Simular cambio de datos según el período
      console.log('Cambiando período a:', period)
      
      // Aquí se actualizarían los datos según el período seleccionado
      // Por ahora solo actualizamos el texto del período
      const periodLabels = {
        'current_month': 'Enero 2024',
        'last_month': 'Diciembre 2023',
        'current_quarter': 'Q1 2024',
        'current_year': '2024',
        'custom': 'Período Personalizado'
      }
      
      this.currentPeriod = periodLabels[period] || 'Enero 2024'
      
      // Simular actualización de métricas
      this.refreshData()
      
             // Actualizar datos del gráfico
       this.updateChartData(period)
       this.updateTopProductsData(period)
       this.updateTopCustomersData(period)
       this.updateTopSellersData(period)
    },
    
    generateChartData(period) {
      // Generar datos mock según el período
      const labels = []
      const confirmed = []
      const quoted = []
      
      switch(period) {
        case 'current_month':
          // 30 días del mes actual
          for (let i = 1; i <= 30; i++) {
            labels.push(`${i} Ene`)
            confirmed.push(Math.floor(Math.random() * 2000000) + 500000)
            quoted.push(Math.floor(Math.random() * 2500000) + 600000)
          }
          break
          
        case 'last_month':
          // 30 días del mes anterior
          for (let i = 1; i <= 30; i++) {
            labels.push(`${i} Dic`)
            confirmed.push(Math.floor(Math.random() * 1800000) + 400000)
            quoted.push(Math.floor(Math.random() * 2200000) + 500000)
          }
          break
          
        case 'current_quarter':
          // 12 semanas del trimestre
          for (let i = 1; i <= 12; i++) {
            labels.push(`Sem ${i}`)
            confirmed.push(Math.floor(Math.random() * 8000000) + 2000000)
            quoted.push(Math.floor(Math.random() * 10000000) + 2500000)
          }
          break
          
        case 'current_year':
          // 12 meses del año
          const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
          months.forEach(month => {
            labels.push(month)
            confirmed.push(Math.floor(Math.random() * 30000000) + 10000000)
            quoted.push(Math.floor(Math.random() * 35000000) + 12000000)
          })
          break
          
        default:
          // Período personalizado - 7 días
          for (let i = 1; i <= 7; i++) {
            labels.push(`Día ${i}`)
            confirmed.push(Math.floor(Math.random() * 1500000) + 300000)
            quoted.push(Math.floor(Math.random() * 1800000) + 400000)
          }
      }
      
      return {
        labels: labels,
        datasets: [
          {
            label: 'Ventas Confirmadas',
            data: confirmed,
            borderColor: '#1a73e8',
            backgroundColor: 'rgba(26, 115, 232, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 5
          },
          {
            label: 'Ventas Cotizadas',
            data: quoted,
            borderColor: '#9aa0a6',
            backgroundColor: 'rgba(154, 160, 166, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 5
          }
        ]
      }
    },
    
    updateChartData(period) {
      this.chartData = this.generateChartData(period)
      this.chartOptions = this.getChartOptions()
    },

              updateTopProductsData(period) {
       let products = [];
       
       switch(period) {
         case 'current_month':
           products = [
             { name: 'Línea Industrial', revenue: 25000000, percentage: 28 },
             { name: 'Línea Comercial', revenue: 22000000, percentage: 25 },
             { name: 'Línea Residencial', revenue: 18000000, percentage: 20 },
             { name: 'Línea Agrícola', revenue: 15000000, percentage: 17 },
             { name: 'Línea Automotriz', revenue: 8000000, percentage: 9 }
           ];
           break;
         case 'last_month':
           products = [
             { name: 'Línea Industrial', revenue: 22000000, percentage: 26 },
             { name: 'Línea Comercial', revenue: 20000000, percentage: 24 },
             { name: 'Línea Residencial', revenue: 17000000, percentage: 20 },
             { name: 'Línea Agrícola', revenue: 14000000, percentage: 17 },
             { name: 'Línea Automotriz', revenue: 7000000, percentage: 8 }
           ];
           break;
         case 'current_quarter':
           products = [
             { name: 'Línea Industrial', revenue: 65000000, percentage: 27 },
             { name: 'Línea Comercial', revenue: 58000000, percentage: 24 },
             { name: 'Línea Residencial', revenue: 48000000, percentage: 20 },
             { name: 'Línea Agrícola', revenue: 42000000, percentage: 17 },
             { name: 'Línea Automotriz', revenue: 25000000, percentage: 10 }
           ];
           break;
         case 'current_year':
           products = [
             { name: 'Línea Industrial', revenue: 250000000, percentage: 28 },
             { name: 'Línea Comercial', revenue: 220000000, percentage: 25 },
             { name: 'Línea Residencial', revenue: 180000000, percentage: 20 },
             { name: 'Línea Agrícola', revenue: 150000000, percentage: 17 },
             { name: 'Línea Automotriz', revenue: 80000000, percentage: 9 }
           ];
           break;
         default:
           products = [
             { name: 'Línea Industrial', revenue: 20000000, percentage: 26 },
             { name: 'Línea Comercial', revenue: 18000000, percentage: 24 },
             { name: 'Línea Residencial', revenue: 15000000, percentage: 20 },
             { name: 'Línea Agrícola', revenue: 12000000, percentage: 16 },
             { name: 'Línea Automotriz', revenue: 7000000, percentage: 9 }
           ];
        }
        
               this.topProductsList = products;
     },
     
     updateTopCustomersData(period) {
       let customers = [];
       
       switch(period) {
         case 'current_month':
           customers = [
             { name: 'Cliente Industrial A', revenue: 15000000, percentage: 18 },
             { name: 'Cliente Comercial B', revenue: 12000000, percentage: 14 },
             { name: 'Cliente Residencial C', revenue: 10000000, percentage: 12 },
             { name: 'Cliente Agrícola D', revenue: 8000000, percentage: 10 },
             { name: 'Cliente Automotriz E', revenue: 6000000, percentage: 7 }
           ];
           break;
         case 'last_month':
           customers = [
             { name: 'Cliente Industrial X', revenue: 13000000, percentage: 16 },
             { name: 'Cliente Comercial Y', revenue: 11000000, percentage: 13 },
             { name: 'Cliente Residencial Z', revenue: 9000000, percentage: 11 },
             { name: 'Cliente Agrícola W', revenue: 7000000, percentage: 9 },
             { name: 'Cliente Automotriz V', revenue: 5000000, percentage: 6 }
           ];
           break;
         case 'current_quarter':
           customers = [
             { name: 'Cliente Industrial 1', revenue: 40000000, percentage: 17 },
             { name: 'Cliente Comercial 2', revenue: 35000000, percentage: 15 },
             { name: 'Cliente Residencial 3', revenue: 30000000, percentage: 13 },
             { name: 'Cliente Agrícola 4', revenue: 25000000, percentage: 11 },
             { name: 'Cliente Automotriz 5', revenue: 20000000, percentage: 9 }
           ];
           break;
         case 'current_year':
           customers = [
             { name: 'Cliente Industrial 2023', revenue: 150000000, percentage: 18 },
             { name: 'Cliente Comercial 2023', revenue: 130000000, percentage: 16 },
             { name: 'Cliente Residencial 2023', revenue: 110000000, percentage: 13 },
             { name: 'Cliente Agrícola 2023', revenue: 90000000, percentage: 11 },
             { name: 'Cliente Automotriz 2023', revenue: 70000000, percentage: 9 }
           ];
           break;
         default:
           customers = [
             { name: 'Cliente Industrial 1', revenue: 12000000, percentage: 16 },
             { name: 'Cliente Comercial 2', revenue: 10000000, percentage: 13 },
             { name: 'Cliente Residencial 3', revenue: 8000000, percentage: 11 },
             { name: 'Cliente Agrícola 4', revenue: 6000000, percentage: 8 },
             { name: 'Cliente Automotriz 5', revenue: 4000000, percentage: 5 }
           ];
       }
       
       this.topCustomersList = customers;
     },
     
     updateTopSellersData(period) {
       let sellers = [];
       
       switch(period) {
         case 'current_month':
           sellers = [
             { name: 'Vendedor A', revenue: 8000000, percentage: 15 },
             { name: 'Vendedor B', revenue: 7000000, percentage: 13 },
             { name: 'Vendedor C', revenue: 6000000, percentage: 11 },
             { name: 'Vendedor D', revenue: 5000000, percentage: 9 },
             { name: 'Vendedor E', revenue: 4000000, percentage: 7 }
           ];
           break;
         case 'last_month':
           sellers = [
             { name: 'Vendedor X', revenue: 7000000, percentage: 14 },
             { name: 'Vendedor Y', revenue: 6000000, percentage: 12 },
             { name: 'Vendedor Z', revenue: 5000000, percentage: 10 },
             { name: 'Vendedor W', revenue: 4000000, percentage: 8 },
             { name: 'Vendedor V', revenue: 3000000, percentage: 6 }
           ];
           break;
         case 'current_quarter':
           sellers = [
             { name: 'Vendedor 1', revenue: 20000000, percentage: 16 },
             { name: 'Vendedor 2', revenue: 18000000, percentage: 14 },
             { name: 'Vendedor 3', revenue: 16000000, percentage: 13 },
             { name: 'Vendedor 4', revenue: 14000000, percentage: 11 },
             { name: 'Vendedor 5', revenue: 12000000, percentage: 10 }
           ];
           break;
         case 'current_year':
           sellers = [
             { name: 'Vendedor 2023-01', revenue: 80000000, percentage: 17 },
             { name: 'Vendedor 2023-02', revenue: 70000000, percentage: 15 },
             { name: 'Vendedor 2023-03', revenue: 60000000, percentage: 13 },
             { name: 'Vendedor 2023-04', revenue: 50000000, percentage: 11 },
             { name: 'Vendedor 2023-05', revenue: 40000000, percentage: 9 }
           ];
           break;
         default:
           sellers = [
             { name: 'Vendedor 1', revenue: 6000000, percentage: 14 },
             { name: 'Vendedor 2', revenue: 5000000, percentage: 12 },
             { name: 'Vendedor 3', revenue: 4000000, percentage: 10 },
             { name: 'Vendedor 4', revenue: 3000000, percentage: 7 },
             { name: 'Vendedor 5', revenue: 2000000, percentage: 5 }
           ];
       }
       
       this.topSellersList = sellers;
     },

    
    
    getChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleFontColor: '#fff',
          bodyFontColor: '#fff',
          borderColor: '#e8eaed',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            label: (tooltipItem, data) => {
              return data.datasets[tooltipItem.datasetIndex].label + ': $' + numeral(tooltipItem.yLabel).format('0,0')
            }
          }
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              fontColor: '#5f6368',
              fontSize: 11
            }
          }],
          yAxes: [{
            gridLines: {
              color: '#f1f3f4',
              borderDash: [2, 2]
            },
            ticks: {
              fontColor: '#5f6368',
              fontSize: 11,
              callback: function(value) {
                return '$' + numeral(value).format('0,0')
              }
            }
          }]
        },
        elements: {
          point: {
            radius: 3,
            hoverRadius: 5
          }
        }
      }
    },

    

    
    


         handlePeriodChange(newPeriod) {
       console.log('Período seleccionado:', newPeriod)
       if (newPeriod === 'custom') {
         this.showCustomDateDialog = true
         this.customStartDate = null
         this.customEndDate = null
       } else {
         this.updatePeriodData(newPeriod)
       }
     },

    applyCustomDate() {
      if (this.customStartDate && this.customEndDate) {
        const startDate = new Date(this.customStartDate).toISOString().slice(0, 10)
        const endDate = new Date(this.customEndDate).toISOString().slice(0, 10)
        this.selectedPeriod = `custom_${startDate}_${endDate}`
        this.showCustomDateDialog = false
        this.updatePeriodData(this.selectedPeriod)
      }
    },

    handleLogoError(event) {
      event.target.src = '@/assets/logo_fallback.png'; // Ruta de la imagen de fallback
    }
  },
  
  mounted() {
    // Cargar datos iniciales
    this.refreshData()
    
         // Inicializar gráficos
     this.$nextTick(() => {
       this.updateChartData(this.selectedPeriod)
       this.updateTopProductsData(this.selectedPeriod)
       this.updateTopCustomersData(this.selectedPeriod)
       this.updateTopSellersData(this.selectedPeriod)
     })
  },
}
</script>

<style scoped>
.dashboard-container {
  background: #fafafa;
  min-height: 100vh;
  font-family: 'Roboto', sans-serif;
}

.dashboard-header {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 24px 0;
  margin-bottom: 24px;
}

.dashboard-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
}

.dashboard-title {
  display: flex;
  flex-direction: column;
}

.title-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.company-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 6px;
  background: white;
  padding: 3px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8eaed;
}

.dashboard-title h1 {
  color: #1a73e8;
  margin: 0;
  font-weight: 600;
  font-size: 1.75rem;
  letter-spacing: -0.02em;
}

.dashboard-title p {
  color: #5f6368;
  margin: 0;
  font-weight: 400;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-top: 2px;
}

.dashboard-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.period-selector {
  background: transparent;
}

.period-selector .v-input__slot {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  min-height: 36px;
}

.period-selector .v-select__selections {
  color: #5f6368;
  font-size: 0.875rem;
  font-weight: 500;
}

.period-selector .v-input__append-inner {
  color: #5f6368;
}

.refresh-btn {
  color: #5f6368 !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.25px;
}

.refresh-btn:hover {
  background: rgba(95, 99, 104, 0.08) !important;
}

.metrics-container {
  padding: 0 24px 24px 24px;
  gap: 16px;
}

.metric-col {
  margin-bottom: 16px;
}

.metric-card {
  border-radius: 8px;
  border: 1px solid #e8eaed;
  transition: all 0.2s ease;
  cursor: pointer;
  background: white;
}

.metric-card:hover {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  border-color: #dadce0;
}

.metric-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.metric-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.metric-title {
  color: #5f6368;
  font-size: 0.875rem;
  font-weight: 500;
  flex: 1;
}

.metric-body {
  display: flex;
  flex-direction: column;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 500;
  color: #202124;
  margin-bottom: 4px;
  line-height: 1.2;
}

.metric-label {
  font-size: 0.75rem;
  color: #5f6368;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Colores de iconos - Google Analytics 4 style */
.blue-bg {
  background: #4285f4;
}

.orange-bg {
  background: #ff9800;
}

.purple-bg {
  background: #9c27b0;
}

.gray-bg {
  background: #757575;
}

.teal-bg {
  background: #009688;
}

.red-bg {
  background: #f44336;
}

.info-row {
  padding: 0 24px 24px 24px;
}

.info-card {
  background: white;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.info-content {
  display: flex;
  align-items: center;
}

.info-text {
  color: #5f6368;
  font-size: 0.875rem;
  font-weight: 400;
}

/* Responsive - Google Analytics 4 style */
@media (max-width: 960px) {
  .dashboard-header-content {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }
  
  .title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .company-logo {
    width: 32px;
    height: 32px;
  }
  
  .dashboard-title h1 {
    font-size: 1.5rem;
    color: #1a73e8;
  }
  
  .dashboard-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .period-selector {
    max-width: 140px !important;
  }
  
  .metrics-container {
    padding: 0 16px 16px 16px;
    gap: 12px;
  }
  
  .metric-col {
    margin-bottom: 12px;
  }
  
  .metric-card {
    height: 110px !important;
  }
  
  .metric-value {
    font-size: 1.25rem;
  }
}

@media (max-width: 600px) {
  .dashboard-header-content {
    gap: 16px;
  }
  
  .title-row {
    gap: 6px;
  }
  
  .company-logo {
    width: 28px;
    height: 28px;
  }
  
  .dashboard-title h1 {
    font-size: 1.25rem;
    color: #1a73e8;
  }
  
  .dashboard-actions {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .period-selector {
    max-width: 100% !important;
  }
  
  .metrics-container {
    padding: 0 12px 12px 12px;
    gap: 8px;
  }
  
  .metric-col {
    margin-bottom: 8px;
  }
  
  .metric-card {
    height: 100px !important;
  }
  
  .metric-header {
    margin-bottom: 8px;
  }
  
  .metric-icon-wrapper {
    width: 28px;
    height: 28px;
    margin-right: 8px;
  }
  
  .metric-title {
    font-size: 0.8rem;
  }
  
  .metric-value {
    font-size: 1.125rem;
  }
  
  .metric-label {
    font-size: 0.7rem;
  }
}

/* Estilos del Gráfico */
  .trends-chart-card {
    border-radius: 16px;
    border: 1px solid #e8eaed;
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .trends-chart-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  .chart-title {
    padding: 24px 24px 0 24px;
    border-bottom: 1px solid #f1f3f4;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-radius: 16px 16px 0 0;
  }

.chart-legend {
  display: flex;
  gap: 16px;
  align-items: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #5f6368;
}

.legend-color {
  width: 12px;
  height: 3px;
  border-radius: 2px;
}

.legend-color.confirmed {
  background: #1a73e8;
}

.legend-color.quoted {
  background: #9aa0a6;
}



  .chart-container {
    padding: 20px 24px 24px 24px;
    min-height: 350px;
  }
  
  .top-products-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  

  
  .product-item:hover {
    background: #f8f9fa;
    border-color: #dadce0;
  }
  
  .ga-table {
    width: 100%;
  }
  
  .ga-header {
    display: grid;
    grid-template-columns: 40px 1fr 120px 100px;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #e8eaed;
    font-weight: 600;
    font-size: 0.875rem;
    color: #5f6368;
  }
  
  .ga-row {
    display: grid;
    grid-template-columns: 40px 1fr 120px 100px;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #f1f3f4;
    align-items: center;
    transition: background-color 0.2s ease;
  }
  
  .ga-row:hover {
    background-color: #f8f9fa;
  }
  
  .ga-rank {
    font-weight: 500;
    font-size: 0.875rem;
    color: #9aa0a6;
    text-align: center;
  }
  
  .ga-name {
    font-weight: 500;
    font-size: 0.875rem;
    color: #202124;
  }
  
  .ga-value {
    font-weight: 600;
    font-size: 0.875rem;
    color: #202124;
    text-align: right;
  }
  
  .ga-percent {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .ga-bar {
    flex: 1;
    height: 6px;
    background: #e8eaed;
    border-radius: 3px;
    overflow: hidden;
  }
  
  .ga-fill {
    height: 100%;
    background: #1a73e8;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
  
  .ga-percent span {
    font-weight: 600;
    font-size: 0.875rem;
    color: #5f6368;
    min-width: 30px;
  }
  

  
  .progress-bar {
    width: 100%;
    height: 4px;
    background: #e8eaed;
    border-radius: 2px;
    margin-top: 8px;
    overflow: hidden;
  }
  
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #1a73e8 0%, #4285f4 100%);
    border-radius: 2px;
    transition: width 0.3s ease;
  }
  

  
  .product-info {
    flex: 1;
  }
  
  .product-name {
    font-weight: 600;
    color: #202124;
    font-size: 0.95rem;
    letter-spacing: -0.01em;
  }
  
  .product-units {
    color: #5f6368;
    font-size: 0.85rem;
    font-weight: 500;
  }
  
  .product-percentage {
    font-weight: 700;
    color: #1a73e8;
    font-size: 0.9rem;
    margin-left: 16px;
    padding: 4px 8px;
    background: rgba(26, 115, 232, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(26, 115, 232, 0.2);
  }

@media (max-width: 768px) {
  .chart-legend {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }
  
  .chart-title {
    padding: 16px 16px 0 16px;
  }
  
  .chart-container {
    padding: 16px;
    min-height: 300px;
  }
}
</style> 